import type { Modifier, Modifiers } from 'react-day-picker';

import type { MultiDatePickerBodyProps } from './MultiDatePickerBody';
import { InputFocus } from './MultiDatePickerNavbar';

export const multiDatePickerModifiers = {
  defineDisabledDays: ({
    disabledDays,
    startDate,
    endDate,
    focus,
  }: Partial<MultiDatePickerBodyProps>): Modifier[] => {
    const modifiers = [];

    if (disabledDays) {
      modifiers.push(disabledDays);
    }

    if (focus === InputFocus.StartDate && endDate) {
      modifiers.push({ after: endDate.toDate() });
    }

    if (focus === InputFocus.EndDate && startDate) {
      modifiers.push({ before: startDate.toDate() });
    }

    if (focus === InputFocus.None) {
      if (startDate) {
        modifiers.push({ before: startDate.toDate() });
      }
      if (endDate) {
        modifiers.push({ after: endDate.toDate() });
      }
    }

    return modifiers;
  },

  defineSelectedDays: ({
    startDate,
    endDate,
    hoveredDate,
  }: Partial<MultiDatePickerBodyProps>): Modifier => {
    if (startDate && endDate) {
      if (startDate.isSame(endDate, 'day')) {
        return undefined;
      }

      return {
        after: startDate.toDate(),
        before: endDate.toDate(),
      };
    }
    if (startDate && hoveredDate && startDate.isBefore(hoveredDate, 'day')) {
      return {
        after: startDate.toDate(),
        before: hoveredDate.toDate(),
      };
    }
    if (endDate && hoveredDate && hoveredDate.isBefore(endDate, 'day')) {
      return {
        after: hoveredDate.toDate(),
        before: endDate.toDate(),
      };
    }
  },
  defineModifiers: ({
    startDate,
    endDate,
    hoveredDate,
    focus,
  }: Partial<MultiDatePickerBodyProps>): Partial<Modifiers> => {
    const modifiers: { [newModifierName: string]: Modifier[] } = {
      start: [],
      end: [],
    };

    if (!startDate && !endDate) {
      if (!hoveredDate) {
        return {};
      }

      if (focus === InputFocus.StartDate) {
        modifiers.start.push(hoveredDate.toDate());
      }

      if (focus === InputFocus.EndDate) {
        modifiers.end.push(hoveredDate.toDate());
      }

      return modifiers;
    }

    if (startDate) {
      modifiers.start.push(startDate.toDate());

      if (!endDate && hoveredDate && focus === InputFocus.EndDate) {
        if (hoveredDate.isAfter(startDate)) {
          modifiers.end.push(hoveredDate.toDate());
        } else if (hoveredDate.isSame(startDate)) {
          modifiers.end.push(startDate.toDate()); // make it a blue circle
        }
      }
    }

    if (endDate) {
      modifiers.end.push(endDate.toDate());

      if (!startDate && hoveredDate && focus === InputFocus.StartDate) {
        if (hoveredDate.isBefore(endDate)) {
          modifiers.start.push(hoveredDate.toDate());
        } else if (hoveredDate.isSame(endDate)) {
          modifiers.start.push(endDate.toDate()); // make it a blue circle
        }
      }
    }

    return modifiers;
  },
};
